import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Zap, Clock, Terminal, CheckCircle, AlertTriangle, Cpu } from 'lucide-react';
import { useAppContext } from '../../core/App';
import { useTelemetry } from '../../core/hooks/useTelemetry';

// --- THE CYNICAL LOGIC LIBRARY (v1.0) ---
const CYNICAL_LIBRARY = {
  legacy_lag: "Simulating 56k Modem latency... This 10-second lag is a tribute to the patience of a 1996 professional. Use this 'Power Pause' to regulate your cortisol.",
  legacy_save: "Auto-save is a modern luxury. In Legacy mode, your data is as fragile as your focus. Click Save often, or lose your progress to the digital ether.",
  legacy_endurance: "Loading... This delay represents 0.05% of the total wait time your manager faced in their first year on the job. Welcome to the history of persistence.",
  legacy_reward: "Loading bar frozen? It’s not a glitch; it’s an endurance test. If you can’t survive a 15-second loading loop, how will you survive a 4-hour project sync?",
  brutalist_warn: "Recruiter Vision is active. Breaking eye contact (switching tabs) will be interpreted as a failure of focus. You have 3 seconds to return before the session self-destructs.",
  brutalist_machine: "AI Assistance Disabled. You are currently the only intelligence in the room. This struggle is the only proof of mind that machines can't forge.",
  brutalist_death: "Session Reset. Recovery latency is being measured for your anonymized Major Heatmap. In the real world, errors have costs; here, they are just data. How fast can you stand back up?",
  brutalist_tab: "Protocol Breached. By switching tabs, you outsourced your focus. This attempt is logged as 'Low Endurance' for your cohort's aggregate resilience score. Rebuild your agency and try again.",
  jiggle_trigger: "High click density detected. You are clicking for validation, not for answers. Breathe. The 'Drunk Intern' protocol doesn't reward panic.",
  synthesis_win: "Lesson complete. That was objectively annoying. But you did it without a machine crutch. Document this: how does an unassisted win feel compared to an AI shortcut?",
  synthesis_advantage: "You just survived a 1990s-speed loading loop. You’ve proven your attention span is longer than a TikTok. In 2026, that is a Tier-1 competitive advantage.",
  synthesis_identity: "Brutalist Mode Cleared. You faced Permadeath and stayed in the tab. Imposter Syndrome cannot survive this level of documented proof of capability."
};

export default function ResilienceEngine({ children, onModuleComplete, moduleName }) {
  const { currentDifficultyTier } = useAppContext();
  const { transmitTelemetry } = useTelemetry();
  
  const [flowState, setFlowState] = useState('gateway');
  const [cachedGamePayload, setCachedGamePayload] = useState(null); 
  
  // High-fidelity tracking arrays and refs to preserve closure synchronicity
  const clickTimestamps = useRef([]);
  const mountTimeRef = useRef(null);
  const firstInteractionMsRef = useRef(null);
  const totalJigglesRef = useRef(0);
  const tabSwitchesRef = useRef(0);
  
  const [isSimulatingLag, setIsSimulatingLag] = useState(false);
  const [lagMessage, setLagMessage] = useState('');

  const [reflectionText, setReflectionText] = useState('');

  const isBrutalist = currentDifficultyTier === 'brutalist';
  const isLegacy = currentDifficultyTier === 'legacy';

  useEffect(() => {
    const handleGlobalClick = () => {
      // Sensor Cross-Contamination Check: Yield to the core SDK if embedded
      if (window.__JOBVANTAGE_SDK_ACTIVE__) return;
      if (flowState !== 'workspace') return;
      
      const now = Date.now();

      // Kinematic tracking lock: capture strictly the first interactive event
      if (firstInteractionMsRef.current === null && mountTimeRef.current !== null) {
          firstInteractionMsRef.current = now - mountTimeRef.current;
      }

      clickTimestamps.current.push(now);
      clickTimestamps.current = clickTimestamps.current.filter(t => now - t < 2000);
      
      // Silent Tracking: Increment jiggle count for telemetry, but do not punish the user.
      if (clickTimestamps.current.length > 6) {
        clickTimestamps.current = []; 
        totalJigglesRef.current += 1;
      }
    };

    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [flowState]);

  useEffect(() => {
    if (!isBrutalist || flowState !== 'workspace') return;

    const handleVisibilityChange = () => {
      // Sensor Cross-Contamination Check: Yield to the core SDK if embedded
      if (window.__JOBVANTAGE_SDK_ACTIVE__) return;
      
      if (document.hidden) {
        // Silent Tracking: Log the context shift, but do not punish the user.
        tabSwitchesRef.current += 1;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleVisibilityChange);
    };
  }, [isBrutalist, flowState]);

  const handleChildComplete = (payload) => {
    setCachedGamePayload(payload); 
    
    if (isLegacy) {
      setIsSimulatingLag(true);
      const messages = [CYNICAL_LIBRARY.legacy_lag, CYNICAL_LIBRARY.legacy_endurance, CYNICAL_LIBRARY.legacy_reward];
      setLagMessage(messages[Math.floor(Math.random() * messages.length)]);
      
      setTimeout(() => {
        setIsSimulatingLag(false);
        setFlowState('synthesis');
      }, 8000); 
    } else {
      setFlowState('synthesis');
    }
  };

  const getModulePrompt = () => {
    if (moduleName && moduleName.toLowerCase().includes('budget')) {
      return "Identify one 'Want' you had to sacrifice to stay out of the Budget Redline. Why was that frustrating?";
    }
    if (moduleName && moduleName.toLowerCase().includes('resume')) {
      return "Which 'Struggle Phase' keyword did you miss that the AI caught? Why didn't you see it as a priority?";
    }
    return "What was the most frustrating part of this task?";
  };

  const submitSynthesis = () => {
    if (reflectionText.trim().length < 10) {
      alert("Reflection too short. Metacognition requires effort.");
      return;
    }
    
    const telemetryPayload = {
      moduleId: moduleName || 'resilience-synthesis',
      rawInteractions: [
        {
          status: 'success',
          tier: currentDifficultyTier || 'modern',
          reflectionLength: reflectionText ? reflectionText.trim().length : 0,
          interactionType: 'synthesis',
          recoveryLatencyMs: 0, 
          jiggleCount: Number(totalJigglesRef.current) || 0,
          tabSwitches: Number(tabSwitchesRef.current) || 0,
          timeToFirstInteractionMs: Number(firstInteractionMsRef.current) || 0 
        }
      ]
    };

    if (typeof transmitTelemetry === 'function') {
        transmitTelemetry(telemetryPayload).catch(e => console.warn("[ResilienceEngine] Telemetry dropped:", e));
    }

    if (typeof onModuleComplete === 'function') {
        const finalPayload = cachedGamePayload 
            ? { ...cachedGamePayload, reflection: reflectionText } 
            : { reflection: reflectionText };
            
        onModuleComplete(finalPayload); 
    } else {
        console.warn("[ResilienceEngine] Callback prop 'onModuleComplete' is undefined. Bypassing execution to prevent crash.");
    }
  };

  return (
    <div className="relative w-full h-full min-h-[600px] overflow-hidden bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 font-sans">
      
      <AnimatePresence>
        {flowState === 'gateway' && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8 bg-slate-900 text-slate-200 text-center"
          >
            <div className={`p-4 rounded-full mb-6 ${isBrutalist ? 'bg-red-500/20 text-red-500' : isLegacy ? 'bg-amber-500/20 text-amber-500' : 'bg-blue-500/20 text-blue-500'}`}>
              <ShieldAlert size={48} />
            </div>
            <h1 className="text-3xl font-black uppercase tracking-widest mb-4">
              {currentDifficultyTier || 'Modern'} Protocol Active
            </h1>
            <div className="max-w-md bg-black/40 p-6 rounded-xl border border-slate-700 text-sm leading-relaxed mb-8 font-mono">
              {isBrutalist ? CYNICAL_LIBRARY.brutalist_warn : isLegacy ? CYNICAL_LIBRARY.legacy_save : "Standard auto-save and AI interventions enabled. Good luck."}
            </div>
            <button 
              onClick={() => {
                  setFlowState('workspace');
                  mountTimeRef.current = Date.now();
                  firstInteractionMsRef.current = null;
                  totalJigglesRef.current = 0;
                  tabSwitchesRef.current = 0;
              }}
              className={`px-8 py-4 rounded-xl font-black uppercase tracking-wider transition-all transform hover:scale-105 ${isBrutalist ? 'bg-red-600 hover:bg-red-500 text-white' : isLegacy ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
            >
              Acknowledge & Begin
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSimulatingLag && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[80] bg-slate-900/95 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center"
          >
            <Clock size={48} className="text-amber-500 mb-6 animate-spin-slow" />
            <div className="w-64 h-2 bg-slate-800 rounded-full overflow-hidden mb-6">
              <motion.div 
                initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 8, ease: "linear" }}
                className="h-full bg-amber-500"
              />
            </div>
            <p className="text-amber-200 font-mono text-sm max-w-md leading-relaxed">
              {lagMessage}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`w-full h-full bg-white transition-opacity duration-500 ${flowState !== 'workspace' ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { onComplete: handleChildComplete });
          }
          return child;
        })}
      </div>

      <AnimatePresence>
        {flowState === 'synthesis' && (
          <motion.div 
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }}
            className="absolute inset-0 z-[90] bg-slate-100 flex flex-col items-center justify-center p-6"
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-emerald-100 text-emerald-600 rounded-xl"><CheckCircle size={24} /></div>
                <div>
                  <h2 className="text-2xl font-black text-slate-800">Task Validated</h2>
                  <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Synthesis Required</p>
                </div>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-sm text-slate-700 italic mb-6">
                "{isBrutalist ? CYNICAL_LIBRARY.synthesis_identity : isLegacy ? CYNICAL_LIBRARY.synthesis_advantage : CYNICAL_LIBRARY.synthesis_win}"
              </div>

              <label className="block font-bold text-slate-700 mb-2">
                {getModulePrompt()}
              </label>
              <textarea 
                value={reflectionText}
                onChange={(e) => setReflectionText(e.target.value)}
                placeholder="Type your metacognitive reflection here..."
                className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl mb-6 focus:ring-2 focus:ring-emerald-500 outline-none resize-none h-32"
              />

              <button 
                onClick={submitSynthesis}
                className="w-full py-4 bg-slate-900 text-white font-black rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2"
              >
                <Cpu size={18} /> Process Memory & Claim Rewards
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}